import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | 'full';
  showCloseButton?: boolean;
  isTitleBordered?: boolean;
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  full: 'max-w-[95vw]',
};

export const Modal = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  showCloseButton = true,
  isTitleBordered = true,
}: ModalProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  // Handle click outside
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        overlayRef.current === event.target ||
        (overlayRef.current?.contains(event.target as Node) &&
          !contentRef.current?.contains(event.target as Node))
      ) {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown, handleClickOutside]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[150] flex items-center justify-center p-4 sm:p-6 bg-navy-900/40 dark:bg-black/60 backdrop-blur-md animate-in fade-in duration-300"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      aria-describedby={description ? 'modal-description' : undefined}
    >
      <div
        ref={contentRef}
        className={`
          relative w-full ${sizeClasses[size]}
          bg-white dark:bg-surface border border-grey-200 dark:border-border rounded-2xl
          shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] dark:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] 
          overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-2 duration-300
          flex flex-col max-h-[90vh] ring-1 ring-black/5 dark:ring-white/5
        `}
      >
        {(title || showCloseButton) && (
          <div className={`relative flex items-start justify-between px-6 pt-6 pb-4 ${isTitleBordered ? 'border-b border-grey-100 dark:border-border' : ''} flex-shrink-0 bg-white/50 dark:bg-surface backdrop-blur-sm z-10`}>
            {title && (
              <div className="pr-8">
                <h2 id="modal-title" className="text-xl font-semibold text-grey-900 dark:text-text-primary tracking-tight">
                  {title}
                </h2>
                {description && (
                  <p id="modal-description" className="mt-1.5 text-sm text-grey-500 dark:text-text-secondary leading-relaxed">
                    {description}
                  </p>
                )}
              </div>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="absolute right-4 top-4 p-2 text-grey-400 dark:text-text-tertiary hover:text-grey-900 dark:hover:text-text-primary hover:bg-grey-100 dark:hover:bg-surface-elevated rounded-full transition-all duration-200"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div data-lenis-prevent className={`relative overflow-y-auto z-10 ${!title && !showCloseButton ? 'p-6' : 'px-6 pb-6 pt-2'}`}>{children}</div>
      </div>
    </div>,
    document.body
  );
};

interface ModalActionsProps {
  children: React.ReactNode;
  className?: string;
}

export const ModalActions = ({ children, className = '' }: ModalActionsProps) => (
  <div className={`flex items-center justify-end gap-3 mt-6 pt-5 border-t border-grey-100 dark:border-border bg-grey-50/50 dark:bg-black/20 -mx-6 px-6 -mb-6 pb-6 ${className}`}>
    {children}
  </div>
);

export default Modal;
